import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Layout } from '@/components/Layout';
import { CitySearchSelect } from '@/components/CitySearchSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { PhotoThumb } from '@/components/PhotoThumb';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { animalsPublicApi } from '@/features/public/animalsPublicApi';
import { bannersPublicApi, type PublicBanner } from '@/features/public/bannersPublicApi';
import { toAbsoluteUrl } from '@/lib/url';
import { Heart, LogOut, User as UserIcon, ChevronLeft, ChevronRight, X, Menu, Calendar, MapPin, Share2, Phone, Globe, Mail, MessageCircle, Loader2, ChevronDown, Filter, BellRing, Instagram, Youtube, Facebook, Twitter, Search } from 'lucide-react';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { formatAnimalAge } from '@/lib/animalAge';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { adoptionApi } from '@/features/adoption/api';
import { shelterSignupsApi } from '@/features/shelter-signups/api';

type PublicAnimal = {
  id: number;
  name?: string;
  speciesId?: number; speciesLabel?: string; species?: { id: number; name?: string };
  sexId?: number; sexLabel?: string; sex?: { id: number; name?: string };
  sizeId?: number; sizeLabel?: string; size?: { id: number; name?: string };
  breedId?: number; breedLabel?: string; breed?: { id: number; name?: string };
  temperamentId?: number; temperamentLabel?: string; temperament?: { id: number; label?: string; name?: string };
  statusId?: number; statusLabel?: string;
  shelterId?: number; shelterName?: string; shelter?: { id: number; name?: string; city?: { name?: string; department?: string } };
  approxAgeMonths?: number;
  primaryPhotoUrl?: string;
  photos?: Array<{ id: number; url: string; isPrimary?: boolean; is_primary?: boolean }>;
};

type ActiveLookups = {
  species?: Array<{ id: number; code?: string; label?: string; name?: string }>;
  sexes?: Array<{ id: number; code?: string; label?: string; name?: string }>;
  sizes?: Array<{ id: number; code?: string; label?: string; name?: string }>;
  breeds?: Array<{ id: number; code?: string; label?: string; name?: string; speciesId?: number; species_id?: number }>;
  temperaments?: Array<{ id: number; code?: string; label?: string; name?: string }>;
  shelters?: Array<{ id: number; name: string }>;
};

type DetailPhoto = {
  id: number;
  url: string;
  isPrimary?: boolean;
  isSizeGuide?: boolean;
};

type AdoptPageProps = {
  forcedShelter?: {
    id: number;
    name?: string;
    slug?: string;
    logotipo?: string;
    banner?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
    websiteUrl?: string;
    instagram?: string;
    facebook?: string;
    twitter?: string;
    tiktok?: string;
    whatsapp?: string;
    city?: { name?: string; department?: string };
  } | null;
};

function createRandomSeed() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function AdoptPage({ forcedShelter }: AdoptPageProps) {
  const { toast } = useToast();
  const { isAuthenticated, user, logout, isCustomer } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [includeInAdoption, setIncludeInAdoption] = useState(false);
  const [sort, setSort] = useState<'createdAt' | 'name' | 'approxAgeMonths' | 'id'>('createdAt');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [data, setData] = useState<{ data: PublicAnimal[]; total: number; page: number; pageSize: number }>({ data: [], total: 0, page: 1, pageSize: 20 });
  const { data: lookups } = useLookupsSnapshot();
  const [activeLookups, setActiveLookups] = useState<ActiveLookups | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isWaitRequestInfoOpen, setIsWaitRequestInfoOpen] = useState(false);
  const [isShelterSignupOpen, setIsShelterSignupOpen] = useState(false);
  const [isSubmittingShelterSignup, setIsSubmittingShelterSignup] = useState(false);
  const [shelterSignup, setShelterSignup] = useState({
    shelterName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    cityId: '',
  });
  const [heroCarouselApi, setHeroCarouselApi] = useState<CarouselApi | null>(null);
  const [publicBanners, setPublicBanners] = useState<PublicBanner[]>([]);
  const [randomSeed, setRandomSeed] = useState(() => createRandomSeed());
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Filters derived from results (simple and public-safe)
  const forcedShelterId = forcedShelter?.id ? Number(forcedShelter.id) : undefined;
  const [filters, setFilters] = useState<{ speciesId?: number; sexId?: number; sizeId?: number; breedId?: number; temperamentId?: number; shelterId?: number; ageMin?: number; ageMax?: number }>(() => ({
    shelterId: forcedShelterId,
  }));
  const hasCatalogFilters = useMemo(
    () =>
      !!debouncedQ ||
      !!filters.speciesId ||
      !!filters.sexId ||
      !!filters.sizeId ||
      !!filters.breedId ||
      !!filters.shelterId ||
      !!filters.temperamentId ||
      filters.ageMin != null ||
      filters.ageMax != null,
    [debouncedQ, filters],
  );
  const previousHasCatalogFilters = useRef(hasCatalogFilters);
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        q: debouncedQ,
        includeInAdoption,
        sort: hasCatalogFilters ? sort : 'random',
        dir,
        filters,
        hasCatalogFilters,
        randomSeed,
      }),
    [debouncedQ, includeInAdoption, sort, dir, filters, hasCatalogFilters, randomSeed],
  );
  const previousRequestKey = useRef(requestKey);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!heroCarouselApi) return;
    const interval = setInterval(() => {
      heroCarouselApi.scrollNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [heroCarouselApi]);

  useEffect(() => {
    if (forcedShelter) return;
    let cancelled = false;
    const loadBanners = async () => {
      try {
        const items = await bannersPublicApi.list();
        if (!cancelled) setPublicBanners(items || []);
      } catch {
        if (!cancelled) setPublicBanners([]);
      }
    };
    loadBanners();
    return () => {
      cancelled = true;
    };
  }, [forcedShelter]);

  useEffect(() => {
    if (!forcedShelterId) return;
    setFilters((prev) => (prev.shelterId === forcedShelterId ? prev : { ...prev, shelterId: forcedShelterId }));
  }, [forcedShelterId]);

  useEffect(() => {
    if (!hasCatalogFilters && previousHasCatalogFilters.current) {
      setRandomSeed(createRandomSeed());
    }
    previousHasCatalogFilters.current = hasCatalogFilters;
  }, [hasCatalogFilters]);

  // Carregar lookups ativos (apenas valores utilizados nos cadastros públicos)
  useEffect(() => {
    let cancelled = false;
    const fetchActiveLookups = async () => {
      try {
        const res = await animalsPublicApi.activeLookups(includeInAdoption);
        if (!cancelled) {
          setActiveLookups(res || {});
        }
      } catch {
        if (!cancelled) {
          setActiveLookups(null);
        }
      }
    };
    fetchActiveLookups();
    return () => {
      cancelled = true;
    };
  }, [includeInAdoption]);

  useEffect(() => {
    const fetchData = async () => {
      if (previousRequestKey.current !== requestKey) {
        previousRequestKey.current = requestKey;
        setHasMore(true);
        setData((prev) => ({...prev, data: [], total: 0, page: 1, pageSize}));
        if (page !== 1) {
          setPage(1);
          return;
        }
      }
      const isLoadMore = page > 1;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const params: Record<string, any> = {
          page,
          pageSize,
          q: debouncedQ,
          sort: hasCatalogFilters ? sort : 'random',
          dir,
          includeInAdoption,
        };
        if (!hasCatalogFilters) params.randomSeed = randomSeed;
        if (filters.speciesId) params.speciesId = filters.speciesId;
        if (filters.sexId) params.sexId = filters.sexId;
        if (filters.sizeId) params.sizeId = filters.sizeId;
        if (filters.breedId) params.breedId = filters.breedId;
        if (filters.shelterId) params.shelterId = filters.shelterId;
        if (filters.temperamentId) params.temperamentId = filters.temperamentId;
        if (filters.ageMin != null) params.ageMin = filters.ageMin;
        if (filters.ageMax != null) params.ageMax = filters.ageMax;
        const res = await animalsPublicApi.search(params);
        const body = res && typeof res === 'object' ? res : { data: [] };
        const incoming = (body.data || []) as PublicAnimal[];
        const total = Number(body.total ?? incoming.length);
        const nextPage = Number(body.page ?? page);
        const nextPageSize = Number(body.pageSize ?? pageSize);
        setHasMore(nextPage * nextPageSize < total);
        setData((prev) => {
          if (!isLoadMore) {
            return {
              data: incoming,
              total,
              page: nextPage,
              pageSize: nextPageSize,
            };
          }
          const seen = new Set(prev.data.map((animal) => animal.id));
          const merged = [...prev.data];
          for (const animal of incoming) {
            if (!seen.has(animal.id)) {
              seen.add(animal.id);
              merged.push(animal);
            }
          }
          return {
            data: merged,
            total,
            page: nextPage,
            pageSize: nextPageSize,
          };
        });
      } catch (e: any) {
        setError(e?.response?.data?.error?.message || 'Failed to load animals');
      } finally {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [page, pageSize, debouncedQ, includeInAdoption, sort, dir, filters, hasCatalogFilters, randomSeed]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || loading || loadingMore || !hasMore || !!error) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setPage((current) => current + 1);
        }
      },
      {rootMargin: '600px 0px'},
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, error, requestKey, data.data.length]);

  const forcedBannerUrl = toAbsoluteUrl(forcedShelter?.banner);
  const forcedLogoUrl = toAbsoluteUrl(forcedShelter?.logotipo);
  const forcedCity = forcedShelter?.city;
  const forcedShareUrl = typeof window === 'undefined' ? '/adopt' : window.location.href;
  const forcedSocials = {
    whatsapp: forcedShelter?.whatsapp,
    instagram: forcedShelter?.instagram,
    facebook: forcedShelter?.facebook,
    tiktok: forcedShelter?.tiktok,
    twitter: forcedShelter?.twitter,
  };

  // Lookup helpers to fill missing labels coming from API
  const speciesLabelOf = (a: PublicAnimal): string => {
    const id = a.speciesId ?? a.species?.id;
    const rel = (a as any).species;
    const fromRel = rel?.label || rel?.name;
    if (a.speciesLabel) return a.speciesLabel;
    if (fromRel) return fromRel;
    const found = (activeLookups?.species || lookups?.species || []).find(s => s.id === id);
    return found ? toOptionLabel(found) : '-';
  };
  const breedLabelOf = (a: PublicAnimal): string | undefined => {
    const id = a.breedId ?? a.breed?.id;
    const rel = (a as any).breed;
    const fromRel = rel?.label || rel?.name;
    if (a.breedLabel) return a.breedLabel;
    if (fromRel) return fromRel;
    const found = (activeLookups?.breeds || lookups?.breeds || []).find(b => b.id === id);
    return found ? toOptionLabel(found) : undefined;
  };
  const shelterLabelOf = (a: PublicAnimal): string | undefined => {
    const rel = (a as any).shelter;
    return a.shelterName || rel?.name || undefined;
  };
  const sexLabelOf = (a: PublicAnimal): string => {
    const id = a.sexId ?? a.sex?.id;
    const rel = (a as any).sex;
    const fromRel = rel?.label || rel?.name;
    if (a.sexLabel) return a.sexLabel;
    if (fromRel) return fromRel;
    const found = (activeLookups?.sexes || lookups?.sexes || []).find(s => s.id === id);
    return found ? toOptionLabel(found) : '-';
  };
  const sizeLabelOf = (a: PublicAnimal): string => {
    const id = a.sizeId ?? a.size?.id;
    const rel = (a as any).size;
    const fromRel = rel?.label || rel?.name;
    if (a.sizeLabel) return a.sizeLabel;
    if (fromRel) return fromRel;
    const found = (activeLookups?.sizes || lookups?.sizes || []).find(s => s.id === id);
    return found ? toOptionLabel(found) : '-';
  };
  const colorLabelOf = (a: PublicAnimal): string | undefined => {
    const id = (a as any).colorId ?? (a as any).color_id ?? (a as any).color?.id;
    const rel = (a as any).color;
    const fromRel = rel?.label || rel?.name;
    const direct = (a as any).colorLabel;
    if (direct) return direct;
    if (fromRel) return fromRel;
    const found = (lookups?.colors || []).find((c) => c.id === id);
    return found ? toOptionLabel(found) : undefined;
  };
  const temperamentLabelOf = (a: PublicAnimal): string | undefined => {
    const id = a.temperamentId ?? a.temperament?.id;
    const rel = (a as any).temperament;
    const fromRel = rel?.label || rel?.name;
    if (a.temperamentLabel) return a.temperamentLabel;
    if (fromRel) return fromRel;
    const found = (activeLookups?.temperaments || lookups?.temperaments || []).find(t => t.id === id);
    return found ? toOptionLabel(found) : undefined;
  };
  const statusLabelOf = (a: PublicAnimal): string => {
    const rel = (a as any).status;
    const fromRel = rel?.label || rel?.name;
    if (a.statusLabel) return a.statusLabel;
    if (fromRel) return fromRel;
    const id = (a as any).statusId;
    const found = (lookups?.statuses || []).find(s => s.id === id);
    return found ? toOptionLabel(found) : '-';
  };

  // Build filter option lists (id + label) from active lookups (fallback: snapshot/data)
  const speciesOpts = useMemo(() => {
    const src = activeLookups?.species || lookups?.species || [];
    return src.map(i => ({ id: i.id, label: toOptionLabel(i as any) }));
  }, [activeLookups, lookups]);

  const sexOpts = useMemo(() => {
    const src = activeLookups?.sexes || lookups?.sexes || [];
    return src.map(i => ({ id: i.id, label: toOptionLabel(i as any) }));
  }, [activeLookups, lookups]);

  const sizeOpts = useMemo(() => {
    const src = activeLookups?.sizes || lookups?.sizes || [];
    return src.map(i => ({ id: i.id, label: toOptionLabel(i as any) }));
  }, [activeLookups, lookups]);

  const temperamentOpts = useMemo(() => {
    const src = activeLookups?.temperaments || lookups?.temperaments || [];
    return src.map(i => ({ id: i.id, label: toOptionLabel(i as any) }));
  }, [activeLookups, lookups]);

  const shelterOpts = useMemo(() => {
    if (activeLookups?.shelters && activeLookups.shelters.length) {
      const base = activeLookups.shelters.map(s => ({ id: s.id, label: s.name }));
      if (forcedShelterId && !base.some((s) => Number(s.id) === forcedShelterId)) {
        base.unshift({ id: forcedShelterId, label: forcedShelter?.name || `Entidad ${forcedShelterId}` });
      }
      return base;
    }
    // Fallback: derive from current page results (previous behavior)
    const set = new Map<number, string>();
    (data.data || []).forEach(r => {
      const id = r.shelterId ?? r.shelter?.id;
      const label = r.shelterName ?? r.shelter?.name;
      if (id && label) set.set(id, label);
    });
    if (forcedShelterId && !set.has(forcedShelterId)) {
      set.set(forcedShelterId, forcedShelter?.name || `Entidad ${forcedShelterId}`);
    }
    return Array.from(set.entries()).map(([id, label]) => ({ id, label }));
  }, [activeLookups, data, forcedShelterId, forcedShelter?.name]);

  const breedOpts = useMemo(() => {
    const srcBreeds = (activeLookups?.breeds && activeLookups.breeds.length
      ? activeLookups.breeds
      : lookups?.breeds || []);
    const breeds = srcBreeds.map(b => ({ id: b.id, label: toOptionLabel(b as any), raw: b }));
    return filters.speciesId
      ? breeds
        .filter(
          b =>
            (b.raw as any).species_id === filters.speciesId ||
            (b.raw as any).speciesId === filters.speciesId,
        )
        .map(({ id, label }) => ({ id, label }))
      : breeds.map(({ id, label }) => ({ id, label }));
  }, [activeLookups, lookups, filters.speciesId]);

  // Favorites (likes) stored in localStorage
  const FAVORITES_KEY = 'sabuesos_favorites';
  const [likedIds, setLikedIds] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      const arr = raw ? (JSON.parse(raw) as any[]) : [];
      return new Set((arr || []).map((n) => Number(n)));
    } catch {
      return new Set<number>();
    }
  });
  const toggleLike = (id: number) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(next))); } catch { }
      return next;
    });
  };

  const ensureFavorite = (id: number) => {
    setLikedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(next))); } catch { }
      return next;
    });
  };

  const [onlyLiked, setOnlyLiked] = useState(false);

  // Apply favorites filter client-side if enabled
  const filtered = useMemo(() => {
    const arr = data.data || [];
    return onlyLiked ? arr.filter((a) => likedIds.has(a.id)) : arr;
  }, [data, onlyLiked, likedIds]);

  // Modal details
  const [isOpen, setIsOpen] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [photos, setPhotos] = useState<DetailPhoto[]>([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [hasExistingApplication, setHasExistingApplication] = useState(false);
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);

  const getShareUrl = (animalId: number) => {
    if (typeof window === 'undefined') return `/adopt?animalId=${animalId}`;
    const origin = window.location.origin;
    const path = window.location.pathname || '/adopt';
    try {
      const url = new URL(`${origin}${path}`);
      url.searchParams.set('animalId', String(animalId));
      return url.toString();
    } catch {
      return `${origin}${path}?animalId=${animalId}`;
    }
  };

  const openDetails = async (id: number) => {
    try {
      setIsOpen(true);
      setDetail(null);
      setPhotos([]);
      setHasExistingApplication(false);
      const next = new URLSearchParams(searchParams);
      if (next.get('animalId') !== String(id)) {
        next.set('animalId', String(id));
        setSearchParams(next, { replace: true });
      }
      const body = await animalsPublicApi.getWithPhotos(id);
      setDetail(body);
      const list = ((body?.photos || []) as any[]).map((p) => ({ id: p.id, url: toAbsoluteUrl(p.url)!, isPrimary: p.is_primary ?? p.isPrimary }));
      const ordered = [
        ...list.filter(p => p.isPrimary),
        ...list.filter(p => !p.isPrimary),
      ];
      const sizeGuideUrl = getDogSizeGuideUrl(body);
      const withSizeGuide = sizeGuideUrl
        ? [...ordered, {id: -1, url: sizeGuideUrl, isPrimary: false, isSizeGuide: true}]
        : ordered;
      setPhotos(withSizeGuide);
      setPhotoIdx(0);

      // Verificar si el usuario ya tiene una solicitud para este animal
      if (isAuthenticated) {
        const hasApplication = await adoptionApi.checkExistingApplication(id);
        setHasExistingApplication(hasApplication);
      }
    } catch (e) {
      // Keep modal open; show lightweight error
      setDetail({ error: 'Failed to load details' });
    }
  };

  useEffect(() => {
    const paramId = searchParams.get('animalId');
    if (!paramId || isOpen) return;
    const numeric = Number(paramId);
    if (!Number.isNaN(numeric)) {
      openDetails(numeric);
    }
  }, [searchParams, isOpen]);

  const ageLabel = (animal?: PublicAnimal) =>
    formatAnimalAge({
      birthDate: (animal as any)?.birth_date ?? (animal as any)?.birthDate,
      approxAgeMonths: animal?.approxAgeMonths,
    });

  const isDogAnimal = (animal?: PublicAnimal | null) => {
    if (!animal) return false;
    const speciesCode = String((animal as any)?.species?.code || '').toLowerCase();
    const speciesLabel = String(animal.speciesLabel || (animal as any)?.species?.label || (animal as any)?.species?.name || '').toLowerCase();
    return speciesCode === 'dog' || speciesLabel === 'perro' || speciesLabel === 'cachorro' || speciesLabel === 'dog';
  };

  const dogSizeGuideFile = (animal?: PublicAnimal | null) => {
    if (!animal || !isDogAnimal(animal)) return null;
    const sizeCode = String((animal as any)?.size?.code || '').toLowerCase();
    if (sizeCode === 'toy') return 'mini';
    if (sizeCode === 'small') return 'small';
    if (sizeCode === 'medium') return 'middle';
    if (sizeCode === 'large') return 'big';
    if (sizeCode === 'giant') return 'giant';

    const sizeLabel = sizeLabelOf(animal).toLowerCase();
    if (sizeLabel.includes('miniatura') || sizeLabel.includes('toy')) return 'mini';
    if (sizeLabel.includes('peque')) return 'small';
    if (sizeLabel.includes('mediano')) return 'middle';
    if (sizeLabel.includes('grande')) return 'big';
    if (sizeLabel.includes('gigante')) return 'giant';
    return null;
  };

  const getDogSizeGuideUrl = (animal?: PublicAnimal | null) => {
    const file = dogSizeGuideFile(animal);
    return file ? `/dog_size/${file}.jpeg` : null;
  };

  const sizeGuideOverlayPhoto = photos.find((photo) => photo.isSizeGuide);
  const showSizeGuideOverlay =
    photoIdx === 0 &&
    !!sizeGuideOverlayPhoto &&
    !photos[photoIdx]?.isSizeGuide;

  const buildContactLinks = (shelter?: any) => {
    const links: { label: string; href: string; icon: 'whatsapp' | 'instagram' | 'facebook' | 'site' | 'email' | 'phone' }[] = [];
    if (!shelter) return links;
    const normUrl = (u?: string) => {
      if (!u) return undefined;
      try {
        if (/^https?:\/\//i.test(u)) return u;
        return `https://${u.replace(/^\/*/, '')}`;
      } catch { return undefined; }
    };
    const digits = (v?: string) => (v || '').replace(/\D/g, '');
    const wa = shelter.whatsapp || shelter.whatsApp || shelter.phone || shelter.contactPhone;
    const waDigits = digits(wa);
    if (waDigits) links.push({ label: 'WhatsApp', href: `https://wa.me/${waDigits}`, icon: 'whatsapp' });
    const ig = shelter.instagram;
    if (ig) {
      const igUrl = /^https?:/i.test(ig) ? ig : `https://instagram.com/${String(ig).replace(/^@/, '')}`;
      links.push({ label: 'Instagram', href: igUrl, icon: 'instagram' });
    }
    const fb = shelter.facebook;
    if (fb) {
      const fbUrl = /^https?:/i.test(fb) ? fb : `https://facebook.com/${fb}`;
      links.push({ label: 'Facebook', href: fbUrl, icon: 'facebook' });
    }
    const site = shelter.websiteUrl || shelter.website_url;
    const siteUrl = normUrl(site);
    if (siteUrl) links.push({ label: 'Sitio web', href: siteUrl, icon: 'site' });
    const email = shelter.contactEmail || shelter.email;
    if (email) links.push({ label: 'Email', href: `mailto:${email}`, icon: 'email' });
    const phone = digits(shelter.phone || shelter.contactPhone);
    if (phone) links.push({ label: 'Llamar', href: `tel:${phone}`, icon: 'phone' });
    return links;
  };

  const handleAdoptionIntent = async () => {
    if (!detail?.id) return;
    ensureFavorite(detail.id);
    const targetState = {
      animalId: detail.id,
      shelterId: detail.shelterId || detail.shelter?.id,
    };

    if (!isAuthenticated) {
      navigate('/signup', {
        state: {
          returnTo: '/adoption/wizard',
          ...targetState,
        },
      });
      return;
    }

    setIsSubmittingInterest(true);
    try {
      const profile = await adoptionApi.getMyProfile();
      const profileId = (profile as any)?.id;
      if (profileId) {
        const apps = await adoptionApi.listApplications({
          filter: { where: { adopterProfileId: Number(profileId) }, order: ['createdAt DESC'], limit: 1 },
        });
        const hasApps = Array.isArray(apps) && apps.length > 0;
        const destination = hasApps ? '/adoption/confirm' : '/adoption/wizard';
        navigate(destination, { state: targetState });
      } else {
        navigate('/adoption/wizard', { state: targetState });
      }
    } catch {
      navigate('/adoption/wizard', { state: targetState });
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  const waitRequestReturnTo = '/profile?waitRequest=1';
  const goToWaitRequest = () => {
    setIsWaitRequestInfoOpen(false);
    if (isAuthenticated) {
      navigate(isCustomer ? waitRequestReturnTo : '/profile');
      return;
    }
    navigate('/login', { state: { returnTo: waitRequestReturnTo } });
  };

  const submitShelterSignup = async () => {
    if (!shelterSignup.shelterName.trim() || !shelterSignup.contactName.trim() || !shelterSignup.contactPhone.trim() || !shelterSignup.contactEmail.trim() || !shelterSignup.cityId) {
      return;
    }
    setIsSubmittingShelterSignup(true);
    try {
      await shelterSignupsApi.createPublic({
        shelterName: shelterSignup.shelterName.trim(),
        contactName: shelterSignup.contactName.trim(),
        contactPhone: shelterSignup.contactPhone,
        contactEmail: shelterSignup.contactEmail.trim(),
        cityId: Number(shelterSignup.cityId),
      });
      setIsShelterSignupOpen(false);
      setShelterSignup({ shelterName: '', contactName: '', contactPhone: '', contactEmail: '', cityId: '' });
      toast({ title: 'Solicitud enviada', description: 'Te escribiremos pronto para continuar el proceso.' });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo enviar la solicitud.' });
    } finally {
      setIsSubmittingShelterSignup(false);
    }
  };

  return (
    <Layout showHeader={false} showFooter>
      <a
        href="https://vaki.co/es/vaki/sabuesos?utm_source=copy&utm_medium=vaki-page&utm_campaign=v4"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-0 top-1/2 z-50 flex -translate-y-1/2 items-center gap-2 rounded-l-md border border-r-0 border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-lg transition hover:bg-emerald-50 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:px-4 sm:py-3"
        aria-label="Donar a Sabuesos en Vaki"
      >
        <img src="/logovaki.jpg" alt="" className="h-7 w-7 rounded object-contain" />
        <span className="hidden sm:inline">Donar</span>
      </a>
      {forcedShelterId ? (
        <>
          <section className="w-full border-b bg-white">
            <div className="container mx-auto px-6 py-2 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo-sabuesos.png" className="h-6 w-6" />
                <span className="text-sm font-semibold tracking-tight text-slate-700">Sabuesos</span>
              </Link>
              <div className="flex items-center gap-2">
                {!isAuthenticated ? (
                  <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                    <UserIcon className="mr-2 h-4 w-4" /> Login
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Menu className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4" />
                        <span className="truncate">{user?.email}</span>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard">Panel</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={logout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Cerrar Sesión
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </section>
          <section className="relative w-full overflow-hidden group">
            {forcedBannerUrl ? (
              <div className="relative h-44 md:h-52">
                <img
                  src={forcedBannerUrl}
                  alt={forcedShelter?.name || 'Banner'}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-transparent" />
              </div>
            ) : (
              <div className="h-44 md:h-52 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600" />
            )}
            <div className="absolute inset-0">
              <div className="container mx-auto px-6 h-full flex items-center">
                <div className="flex items-center gap-4">
                  {forcedLogoUrl ? (
                    <img
                      src={forcedLogoUrl}
                      alt={forcedShelter?.name || 'Logo'}
                      className="h-16 md:h-20 w-auto object-contain drop-shadow-lg"
                    />
                  ) : (
                    <div className="h-16 w-16 md:h-20 md:w-20 bg-white/90 p-2 shadow-lg flex items-center justify-center text-slate-700 font-semibold">
                      {String(forcedShelter?.name || 'S').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="text-white">
                    {forcedShelter?.name && (
                      <h1 className="text-2xl md:text-3xl font-semibold drop-shadow-lg">{forcedShelter.name}</h1>
                    )}
                    {forcedShelterId && (
                      <Link
                        to={forcedShelter?.slug ? `/${forcedShelter.slug}/encontrados` : `/encontrados?shelterId=${forcedShelterId}`}
                        className="mt-1 inline-flex items-center gap-1.5 text-xs md:text-sm text-white/90 hover:text-white underline underline-offset-2"
                      >
                        <Search className="h-3.5 w-3.5" />
                        Animales encontrados de esta entidad
                      </Link>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute left-6 bottom-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-white/90">
                {forcedCity?.name && (
                  <span>{forcedCity.name}{forcedCity.department ? ` - ${forcedCity.department}` : ''}</span>
                )}
                {forcedShelter?.address && <span>{forcedShelter.address}</span>}
                {forcedShelter?.contactPhone && <span>{forcedShelter.contactPhone}</span>}
                {forcedShelter?.contactEmail && <span>{forcedShelter.contactEmail}</span>}
                {forcedShelter?.websiteUrl && <span>{forcedShelter.websiteUrl}</span>}
              </div>
              <div className="absolute right-6 top-3">
                <div className="flex items-center gap-2">
                  {(forcedSocials.whatsapp || forcedSocials.instagram || forcedSocials.facebook || forcedSocials.tiktok || forcedSocials.twitter) && (
                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs md:text-sm text-white/90">
                      {forcedSocials.whatsapp && (
                        <a href={`https://wa.me/${String(forcedSocials.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                      )}
                      {forcedSocials.instagram && (
                        <a href={`https://instagram.com/${String(forcedSocials.instagram).replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                          <Share2 className="h-4 w-4" /> Instagram
                        </a>
                      )}
                      {forcedSocials.facebook && (
                        <a href={`https://facebook.com/${forcedSocials.facebook}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                          <Share2 className="h-4 w-4" /> Facebook
                        </a>
                      )}
                      {forcedSocials.tiktok && (
                        <a href={`https://tiktok.com/@${String(forcedSocials.tiktok).replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                          <Share2 className="h-4 w-4" /> TikTok
                        </a>
                      )}
                      {forcedSocials.twitter && (
                        <a href={`https://x.com/${String(forcedSocials.twitter).replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                          <Share2 className="h-4 w-4" /> X
                        </a>
                      )}
                    </div>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Compartir"
                        className="h-9 w-9 rounded-full border border-white/60 shadow-sm flex items-center justify-center bg-white/90"
                        title="Compartir"
                      >
                        <Share2 className="h-5 w-5 text-slate-700" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {(() => {
                        const name = forcedShelter?.name || 'esta entidad';
                        const text = encodeURIComponent(`Mira la entidad ${name} en Sabuesos: ${forcedShareUrl}`);
                        const encodedUrl = encodeURIComponent(forcedShareUrl);
                        const wa = `https://wa.me/?text=${text}`;
                        const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                        const ig = `https://www.instagram.com/?url=${encodedUrl}`;
                        return (
                          <>
                            <DropdownMenuItem asChild>
                              <a href={wa} target="_blank" rel="noopener noreferrer">Compartir por WhatsApp</a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={fb} target="_blank" rel="noopener noreferrer">Compartir en Facebook</a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={ig} target="_blank" rel="noopener noreferrer">Abrir en Instagram</a>
                            </DropdownMenuItem>
                          </>
                        );
                      })()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="sticky top-0 z-40 border-b bg-white/95 backdrop-blur-sm">
            <div className="container mx-auto px-6 h-16 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <img src="/logo-sabuesos.png" className="h-10 w-10 shadow-sm" />
                <span className="text-base font-semibold tracking-tight text-slate-700">Sabuesos</span>
              </Link>

              {!isAuthenticated ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white text-[#3B82F6] hover:bg-white/90"
                  onClick={() => navigate('/login')}
                >
                  Iniciar Sesión
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="bg-white text-[#3B82F6] hover:bg-white/90">
                      <Menu className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      <span className="truncate">{user?.email}</span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={isCustomer ? '/adopter/dashboard' : '/dashboard'}>
                        {isCustomer ? 'Mis adopciones' : 'Panel'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </section>

          {publicBanners.length > 0 && (
            <section className="relative w-full bg-slate-100">
              <Carousel className="w-full" opts={{ loop: true }} setApi={setHeroCarouselApi}>
                <CarouselContent>
                  {publicBanners.map((slide) => {
                  const desktopUrl = toAbsoluteUrl(slide.desktop_image_path) || '/banner-sabuesos-1.png';
                  const mobileUrl = toAbsoluteUrl(slide.mobile_image_path) || desktopUrl;
                  const hasLink = !!slide.link_url;
                  const hasTitle = !!slide.title;

                  const media = (
                    <div className="relative h-full w-full">
                      <picture>
                        <source
                          media="(max-width: 767px)"
                          srcSet={`${mobileUrl} 1080w`}
                          sizes="100vw"
                        />
                        <source
                          media="(min-width: 768px)"
                          srcSet={`${desktopUrl} 2400w`}
                          sizes="100vw"
                        />
                        <img
                          src={desktopUrl}
                          alt={slide.title || 'Banner Sabuesos'}
                          className="w-full h-full object-cover object-center"
                          loading="lazy"
                          decoding="async"
                        />
                      </picture>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/35" />
                      {hasTitle && (
                        <div className="absolute inset-0 flex items-center justify-center px-4">
                          <h2
                            className="max-w-[92%] text-center font-semibold text-white leading-tight break-words"
                            style={{
                              fontSize: 'clamp(1rem, 3.2vw, 2.5rem)',
                              textShadow:
                                '0 0 6px rgba(0,0,0,.95), 0 0 16px rgba(0,0,0,.85), 0 2px 22px rgba(0,0,0,.9)',
                            }}
                          >
                            {slide.title}
                          </h2>
                        </div>
                      )}
                    </div>
                  );

                    return (
                      <CarouselItem
                        key={slide.id}
                        className="relative overflow-hidden bg-slate-100 h-[clamp(260px,70vw,520px)] md:h-[clamp(230px,26vw,430px)]"
                      >
                        {hasLink ? (
                          <a
                            href={slide.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block h-full w-full"
                          >
                            {media}
                          </a>
                        ) : (
                          media
                        )}
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
                {(publicBanners.length > 1) && (
                  <>
                    <CarouselPrevious className="left-4 bg-white/20 border-white/40 text-white hover:bg-white/40" />
                    <CarouselNext className="right-4 bg-white/20 border-white/40 text-white hover:bg-white/40" />
                  </>
                )}
              </Carousel>
            </section>
          )}
        </>
      )}

      {/* Background gradient area below hero */}
      <section className="w-full bg-gradient-to-b from-gray-100 via-gray-200 to-gray-300/80 min-h-[500px]">
        <div className="container mx-auto px-4 pt-6 pb-8">
          {/* Filtros Collapsible */}
          <Collapsible
            open={isFiltersOpen}
            onOpenChange={setIsFiltersOpen}
            className="mb-6 space-y-2"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-auto bg-white border-primary/20 hover:bg-white/80 hover:text-primary gap-2">
                    <Filter className="h-4 w-4" />
                    {isFiltersOpen ? 'Ocultar Filtros' : 'Mostrar Filtros de Búsqueda'}
                    <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isFiltersOpen ? "transform rotate-180" : "")} />
                  </Button>
                </CollapsibleTrigger>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
                  onClick={() => setIsWaitRequestInfoOpen(true)}
                >
                  <BellRing className="h-4 w-4" />
                  Aún no encontré mi mascota ideal
                </Button>
                {!forcedShelterId && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto bg-white gap-2"
                    onClick={() => setIsShelterSignupOpen(true)}
                  >
                    ¿Eres una entidad de Bienestar Ánimal? Escríbenos.
                  </Button>
                )}
              </div>

              {/* Show active filters summary when closed */}
              {!isFiltersOpen && (filters.speciesId || filters.sexId || filters.sizeId || filters.temperamentId || filters.shelterId || filters.breedId || filters.ageMin != null || filters.ageMax != null || onlyLiked) && (
                 <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary" /> Filtros activos</span>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setFilters({})}>Limpiar</Button>
                 </div>
              )}
            </div>

            <CollapsibleContent>
              <Card className="p-4 bg-white/90 backdrop-blur-sm mt-2 animate-in slide-in-from-top-2 fade-in duration-300">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <select className="w-full border rounded h-9 px-2" value={filters.speciesId || ''} onChange={(e) => setFilters(f => ({ ...f, speciesId: e.target.value ? Number(e.target.value) : undefined, breedId: undefined }))}>
                      <option value="">Todas las especies</option>
                      {speciesOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <select className="w-full border rounded h-9 px-2" value={filters.sexId || ''} onChange={(e) => setFilters(f => ({ ...f, sexId: e.target.value ? Number(e.target.value) : undefined }))}>
                      <option value="">Todos los sexos</option>
                      {sexOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <select className="w-full border rounded h-9 px-2" value={filters.sizeId || ''} onChange={(e) => setFilters(f => ({ ...f, sizeId: e.target.value ? Number(e.target.value) : undefined }))}>
                      <option value="">Todos los tamaños</option>
                      {sizeOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <select className="w-full border rounded h-9 px-2" value={filters.temperamentId || ''} onChange={(e) => setFilters(f => ({ ...f, temperamentId: e.target.value ? Number(e.target.value) : undefined }))}>
                      <option value="">Todos los temperamentos</option>
                      {temperamentOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <select
                      className="w-full border rounded h-9 px-2"
                      value={(forcedShelterId ?? filters.shelterId) || ''}
                      onChange={(e) => setFilters(f => ({ ...f, shelterId: e.target.value ? Number(e.target.value) : undefined }))}
                      disabled={!!forcedShelterId}
                    >
                      <option value="">Todas las entidades</option>
                      {shelterOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <select className="w-full border rounded h-9 px-2" disabled={!filters.speciesId} value={filters.breedId || ''} onChange={(e) => setFilters(f => ({ ...f, breedId: e.target.value ? Number(e.target.value) : undefined }))}>
                      <option value="">Todas las razas</option>
                      {breedOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Input inputMode="numeric" placeholder="Edad mínima (meses)" value={filters.ageMin ?? ''} onChange={(e) => setFilters(f => ({ ...f, ageMin: e.target.value ? Number(e.target.value) : undefined }))} />
                  </div>
                  <div>
                    <Input inputMode="numeric" placeholder="Edad máxima (meses)" value={filters.ageMax ?? ''} onChange={(e) => setFilters(f => ({ ...f, ageMax: e.target.value ? Number(e.target.value) : undefined }))} />
                  </div>
                  <div className="flex items-center gap-6 mt-6">
                    <div className="flex items-center gap-2">
                      <input id="onlyLiked" type="checkbox" className="h-4 w-4" checked={onlyLiked} onChange={(e) => setOnlyLiked(e.target.checked)} />
                      <label htmlFor="onlyLiked" className="text-sm">Solo favoritos</label>
                    </div>
                  </div>
                </div>

                {/* Active filter chips */}
                {(filters.speciesId || filters.sexId || filters.sizeId || filters.temperamentId || filters.shelterId || filters.breedId || filters.ageMin != null || filters.ageMax != null || onlyLiked) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {filters.speciesId && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, speciesId: undefined, breedId: undefined }))}>
                        Especie <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {filters.sexId && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, sexId: undefined }))}>
                        Sexo <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {filters.sizeId && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, sizeId: undefined }))}>
                        Tamaño <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {filters.temperamentId && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, temperamentId: undefined }))}>
                        Temperamento <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {filters.breedId && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, breedId: undefined }))}>
                        Raza <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {filters.shelterId && !forcedShelterId && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, shelterId: undefined }))}>
                        Entidad <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {(filters.ageMin != null || filters.ageMax != null) && (
                      <Button variant="outline" size="sm" onClick={() => setFilters(f => ({ ...f, ageMin: undefined, ageMax: undefined }))}>
                        Edad: {filters.ageMin ?? 0}-{filters.ageMax ?? '∞'} <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    {onlyLiked && (
                      <Button variant="outline" size="sm" onClick={() => setOnlyLiked(false)}>
                        Favoritos <X className="ml-2 h-3 w-3" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setFilters({})}>Reset</Button>
                  </div>
                )}
                <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Ordenar por</span>
                    <select className="border rounded h-9 px-2" value={sort} onChange={(e) => setSort(e.target.value as any)}>
                      <option value="createdAt">Fecha de creación</option>
                      <option value="name">Nombre</option>
                      <option value="approxAgeMonths">Edad (meses)</option>
                      <option value="id">ID</option>
                    </select>
                    <select className="border rounded h-9 px-2" value={dir} onChange={(e) => setDir(e.target.value as any)}>
                      <option value="desc">Desc</option>
                      <option value="asc">Asc</option>
                    </select>
                  </div>
                </div>
              </Card>
            </CollapsibleContent>
          </Collapsible>

          {/* Grid */}
          {error && (
            <p className="text-destructive mb-4">{error}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="p-4 animate-pulse h-64 bg-white/90" />
              ))
            ) : filtered.length === 0 ? (
              <div className="col-span-full text-center text-muted-foreground py-10">No hay animales disponibles con los criterios seleccionados.</div>
            ) : (
              filtered.map((a) => (
                <Card
                  key={a.id}
                  className="group overflow-hidden rounded-xl transition-all duration-200 hover:shadow-lg border border-transparent hover:border-primary/20 bg-white cursor-pointer"
                  onClick={() => openDetails(a.id)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="relative">
                    <div className="block w-full aspect-video bg-muted overflow-hidden">
                      {(() => {
                        let url = a.primaryPhotoUrl;
                        if (!url && a.photos && a.photos.length) {
                          const ordered = [
                            ...a.photos.filter(p => (p.isPrimary ?? p.is_primary)),
                            ...a.photos.filter(p => !(p.isPrimary ?? p.is_primary)),
                          ];
                          url = ordered[0]?.url;
                        }
                        return url ? (
                          <PhotoThumb
                            src={toAbsoluteUrl(url)}
                            alt={a.name || 'Animal'}
                            className="w-full h-full"
                            imgClassName="transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : null;
                      })()}
                    </div>
                    <button
                      type="button"
                      aria-label="Marcar como favorito"
                      className={cn(
                        'absolute right-2 top-2 z-20 h-9 w-9 rounded-full shadow flex items-center justify-center bg-white'
                      )}
                      onClick={(e) => { e.stopPropagation(); toggleLike(a.id); }}
                      title={likedIds.has(a.id) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                    >
                      <Heart className={cn('h-5 w-5', likedIds.has(a.id) ? 'text-red-500 fill-red-500' : 'text-slate-600')} />
                    </button>
                    {/* Share button */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Compartir"
                          className="absolute right-12 top-2 z-20 h-9 w-9 rounded-full shadow flex items-center justify-center bg-white"
                          onClick={(e) => e.stopPropagation()}
                          title="Compartir"
                        >
                          <Share2 className="h-5 w-5 text-slate-700" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const shareUrl = getShareUrl(a.id);
                          const name = a.name || 'este animal';
                          const text = encodeURIComponent(`Mira a ${name} para adopción en Sabuesos: ${shareUrl}`);
                          const encodedUrl = encodeURIComponent(shareUrl);
                          const wa = `https://wa.me/?text=${text}`;
                          const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                          const ig = `https://www.instagram.com/?url=${encodedUrl}`;
                          return (
                            <>
                              <DropdownMenuItem asChild>
                                <a href={wa} target="_blank" rel="noopener noreferrer">Compartir por WhatsApp</a>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <a href={fb} target="_blank" rel="noopener noreferrer">Compartir en Facebook</a>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <a href={ig} target="_blank" rel="noopener noreferrer">Abrir en Instagram</a>
                              </DropdownMenuItem>
                            </>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">{a.name || 'Animal'}</h3>
                    <p className="text-sm text-muted-foreground truncate">{breedLabelOf(a) || ''}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(() => { const l = sexLabelOf(a); return l ? (<span className="px-2 py-0.5 rounded-full border text-xs">{l}</span>) : null; })()}
                      {(() => { const l = sizeLabelOf(a); return l ? (<span className="px-2 py-0.5 rounded-full border text-xs">{l}</span>) : null; })()}
                      {(() => { const l = colorLabelOf(a); return l ? (<span className="px-2 py-0.5 rounded-full border text-xs">{l}</span>) : null; })()}
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" /> {ageLabel(a)}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground truncate flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> {shelterLabelOf(a) || ''}
                    </p>
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetails(a.id);
                        }}
                      >
                        Ver detalles
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>

          <div className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">Mostrando {filtered.length} de {data.total} resultados</p>
            <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center">
              {loadingMore ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando más animales...
                </div>
              ) : hasMore ? (
                <span className="text-xs text-muted-foreground">Desplázate para cargar más</span>
              ) : data.total > 0 ? (
                <span className="text-xs text-muted-foreground">Ya viste todos los animales disponibles</span>
              ) : null}
            </div>
          </div>

          <section className="mt-10 border-t border-primary/10 pt-8">
            <div className="rounded-lg bg-white/70 p-6 backdrop-blur-sm">
              <div className="grid gap-8 md:grid-cols-3">
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Descarga la app</h3>
                  <div className="flex flex-col gap-3">
                    <Button asChild variant="outline" className="justify-start gap-2 bg-white">
                      <a
                        href="https://apps.apple.com/co/app/sabuesos/id6624294814"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <AppleIcon className="h-4 w-4" />
                        App Store
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="justify-start gap-2 bg-white">
                      <a
                        href="https://play.google.com/store/apps/details?id=co.com.sabuesos.newapp&pcampaignid=web_share"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <GooglePlayIcon className="h-4 w-4" />
                        Google Play
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Síguenos</h3>
                  <div className="flex flex-wrap gap-3">
                    <SocialLink href="https://www.instagram.com/sabuesosapp/" label="Instagram" icon={<Instagram className="h-4 w-4" />} />
                    <SocialLink href="https://www.youtube.com/@SabueSOSApp" label="YouTube" icon={<Youtube className="h-4 w-4" />} />
                    <SocialLink href="https://www.facebook.com/SabuesosApp/" label="Facebook" icon={<Facebook className="h-4 w-4" />} />
                    <SocialLink href="https://x.com/SabuesosApp" label="X" icon={<Twitter className="h-4 w-4" />} />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Información legal</h3>
                  <div className="flex flex-col gap-2 text-sm">
                    <a
                      href="/politica-de-privacidad-y-tratamiento-de-datos-personales"
                      className="text-slate-700 transition hover:text-primary hover:underline"
                    >
                      Política de Privacidad y Tratamiento de Datos Personales
                    </a>
                    <a
                      href="https://sabuesos.co/politica-de-estandares-de-seguridad-infantil/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-700 transition hover:text-primary hover:underline"
                    >
                      Política de estándares de seguridad infantil
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Dialog open={isWaitRequestInfoOpen} onOpenChange={setIsWaitRequestInfoOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Aún no encontraste tu mascota ideal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  A veces encontrar nuestro compañero ideal requiere paciencia y algo de tiempo de espera.
                  En Sabuesos estamos aquí para ayudarte.
                </p>
                <p>
                  Completa los datos del animal que buscas y trataremos de encontrar el match ideal para ti.
                  Cuando aparezca un animal compatible, te avisaremos por correo.
                </p>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {!isAuthenticated && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsWaitRequestInfoOpen(false);
                      navigate('/signup', { state: { returnTo: waitRequestReturnTo } });
                    }}
                  >
                    Crear cuenta
                  </Button>
                )}
                <Button onClick={goToWaitRequest}>
                  <BellRing className="h-4 w-4 mr-2" />
                  Generar alerta
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isShelterSignupOpen} onOpenChange={setIsShelterSignupOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Quiero anunciar animales de mi entidad</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium">Nombre de la entidad</label>
                    <Input value={shelterSignup.shelterName} onChange={(e) => setShelterSignup((prev) => ({...prev, shelterName: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Nombre del responsable</label>
                    <Input value={shelterSignup.contactName} onChange={(e) => setShelterSignup((prev) => ({...prev, contactName: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Celular</label>
                    <Input value={shelterSignup.contactPhone} onChange={(e) => setShelterSignup((prev) => ({...prev, contactPhone: e.target.value.replace(/\D/g, '').slice(0, 20)}))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <Input type="email" value={shelterSignup.contactEmail} onChange={(e) => setShelterSignup((prev) => ({...prev, contactEmail: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Ciudad</label>
                    <CitySearchSelect
                      value={shelterSignup.cityId}
                      onChange={(value) => setShelterSignup((prev) => ({...prev, cityId: String(value)}))}
                      placeholder="Seleccionar ciudad"
                    />
                  </div>
                </div>
                <div className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                  Déjanos tus datos y te contactaremos pronto para revisar la vinculación de tu entidad a SabueSOS.
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsShelterSignupOpen(false)}>Cancelar</Button>
                  <Button
                    onClick={submitShelterSignup}
                    disabled={
                      isSubmittingShelterSignup ||
                      !shelterSignup.shelterName.trim() ||
                      !shelterSignup.contactName.trim() ||
                      !shelterSignup.contactPhone.trim() ||
                      !shelterSignup.contactEmail.trim() ||
                      !shelterSignup.cityId
                    }
                  >
                    {isSubmittingShelterSignup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Enviar solicitud
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Details modal with carousel */}
          <Dialog
            open={isOpen}
            onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) {
                setDetail(null);
                const next = new URLSearchParams(searchParams);
                if (next.has('animalId')) {
                  next.delete('animalId');
                  setSearchParams(next, { replace: true });
                }
              }
            }}
          >
            <DialogContent className="max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto" aria-describedby="animal-dialog-desc">
              <DialogHeader>
                <DialogTitle>{detail?.name || 'Detalles del animal'}</DialogTitle>
              </DialogHeader>
              <p id="animal-dialog-desc" className="sr-only">Información y fotos del animal seleccionado</p>
              <div className="space-y-4">
                {/* Carousel */}
                {photos.length > 0 && (
                  <div className="relative w-full aspect-video bg-muted rounded-xl overflow-hidden">
                    {/* Badge for primary photo */}
                    {photos[photoIdx]?.isPrimary && (
                      <span className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold shadow">
                        Foto Principal
                      </span>
                    )}
                    {photos[photoIdx]?.isSizeGuide && (
                      <span className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full bg-slate-900/85 text-white text-xs font-semibold shadow">
                        Guía de tamaño
                      </span>
                    )}
                    {/* Like + Share on details */}
                    <button
                      type="button"
                      aria-label="Marcar como favorito"
                      className={cn(
                        'absolute right-3 top-3 z-20 h-9 w-9 rounded-full shadow flex items-center justify-center bg-white'
                      )}
                      onClick={(e) => { e.stopPropagation?.(); if (detail?.id) toggleLike(Number(detail.id)); }}
                      title={detail?.id && likedIds.has(Number(detail.id)) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                    >
                      <Heart className={cn('h-5 w-5', detail?.id && likedIds.has(Number(detail.id)) ? 'text-red-500 fill-red-500' : 'text-slate-600')} />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Compartir"
                          className="absolute right-14 top-3 z-20 h-9 w-9 rounded-full shadow flex items-center justify-center bg-white"
                          onClick={(e) => e.stopPropagation?.()}
                          title="Compartir"
                        >
                          <Share2 className="h-5 w-5 text-slate-700" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const shareUrl = detail?.id
                            ? getShareUrl(Number(detail.id))
                            : (typeof window !== 'undefined' ? window.location.href : '/adopt');
                          const name = detail?.name || 'este animal';
                          const text = encodeURIComponent(`Mira a ${name} para adopción en Sabuesos: ${shareUrl}`);
                          const encodedUrl = encodeURIComponent(shareUrl);
                          const wa = `https://wa.me/?text=${text}`;
                          const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                          const ig = `https://www.instagram.com/?url=${encodedUrl}`;
                          return (
                            <>
                              <DropdownMenuItem asChild>
                                <a href={wa} target="_blank" rel="noopener noreferrer">Compartir por WhatsApp</a>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <a href={fb} target="_blank" rel="noopener noreferrer">Compartir en Facebook</a>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <a href={ig} target="_blank" rel="noopener noreferrer">Abrir en Instagram</a>
                              </DropdownMenuItem>
                            </>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <img src={photos[photoIdx]?.url} className="w-full h-full object-contain bg-black/5" />
                    {showSizeGuideOverlay && (
                      <div className="pointer-events-none absolute right-14 top-1/2 z-10 w-[18%] min-w-[88px] max-w-[132px] -translate-y-1/2 rounded-lg bg-white/80 p-1 shadow-xl backdrop-blur-sm sm:right-16 sm:max-w-[150px]">
                        {/* eslint-disable-next-line jsx-a11y/alt-text */}
                        <img
                          src={sizeGuideOverlayPhoto.url}
                          className="h-auto w-full rounded-md object-contain"
                        />
                      </div>
                    )}
                    <button aria-label="Anterior" className="absolute left-3 top-1/2 -translate-y-1/2 bg-white text-foreground/80 hover:text-foreground rounded-full p-2 shadow" onClick={() => setPhotoIdx((photoIdx - 1 + photos.length) % photos.length)}>
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button aria-label="Siguiente" className="absolute right-3 top-1/2 -translate-y-1/2 bg-white text-foreground/80 hover:text-foreground rounded-full p-2 shadow" onClick={() => setPhotoIdx((photoIdx + 1) % photos.length)}>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
                {/* Details */}
                {detail && (
                  <div className="space-y-5">
                    {/* Compact facts */}
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: 'Especie', value: speciesLabelOf(detail as PublicAnimal) },
                        { label: 'Raza', value: breedLabelOf(detail as PublicAnimal) || '-' },
                        { label: 'Sexo', value: sexLabelOf(detail as PublicAnimal) },
                        { label: 'Tamaño', value: sizeLabelOf(detail as PublicAnimal) },
                        { label: 'Color', value: colorLabelOf(detail as PublicAnimal) || '-' },
                        { label: 'Temperamento', value: temperamentLabelOf(detail as PublicAnimal) || '-' },
                        { label: 'Edad', value: ageLabel(detail) },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-2 rounded-full border px-3 py-1 text-xs bg-white">
                          <span className="text-muted-foreground">{item.label}:</span>
                          <span className="font-medium">{item.value}</span>
                        </div>
                      ))}
                    </div>

                    {/* Compact info blocks */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg border bg-muted/40 p-3">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Historia del animal</p>
                        <p className="text-sm leading-snug">{detail.behavior || '—'}</p>
                      </div>

                      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                        <p className="text-xs text-muted-foreground flex items-center gap-2 mb-1">
                          <MapPin className="h-3.5 w-3.5" /> Entidad
                        </p>
                        <p className="text-sm font-semibold">{detail.shelterName || detail.shelter?.name || '-'}</p>
                        {detail.shelter?.city && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {detail.shelter.city.name}{detail.shelter.city.department ? ` - ${detail.shelter.city.department}` : ''}
                          </p>
                        )}
                      </div>
                    </div>

                    {((detail.animalNeeds || detail.animal_needs) || (detail.acceptanceTerms || detail.acceptance_terms)) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-lg border bg-white p-3">
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Necesidades</p>
                          <p className="text-sm leading-snug">{detail.animalNeeds || detail.animal_needs || '—'}</p>
                        </div>
                        <div className="rounded-lg border bg-white p-3">
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Términos de aceptación</p>
                          <p className="text-sm leading-snug">{detail.acceptanceTerms || detail.acceptance_terms || '—'}</p>
                        </div>
                      </div>
                    )}

                    <hr className="my-1" />

                    {/* CTA row */}
                    <div className="flex items-center justify-between gap-3">
                      {!hasExistingApplication ? (
                        <Button
                          className="flex-1"
                          onClick={handleAdoptionIntent}
                          disabled={isSubmittingInterest || (isAuthenticated && !isCustomer)}
                          title={isAuthenticated && !isCustomer ? 'Disponible solo para adoptantes' : undefined}
                        >
                          {isSubmittingInterest ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Heart className="h-4 w-4 mr-2" />
                          )}
                          Me Interesa Adoptar
                        </Button>
                      ) : (
                        <div className="flex-1 bg-muted border rounded-md px-4 py-2 text-sm text-center text-muted-foreground">
                          Ya tienes una solicitud de adopción para este animal
                        </div>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="whitespace-nowrap">
                            <Phone className="h-4 w-4 mr-2" /> Contactar
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {buildContactLinks(detail.shelter).map((l) => (
                            <DropdownMenuItem key={l.label} asChild>
                              <a href={l.href} target="_blank" rel="noopener noreferrer">
                                {l.icon === 'whatsapp' && <MessageCircle className="h-4 w-4 mr-2" />}
                                {l.icon === 'site' && <Globe className="h-4 w-4 mr-2" />}
                                {l.icon === 'email' && <Mail className="h-4 w-4 mr-2" />}
                                {l.icon === 'phone' && <Phone className="h-4 w-4 mr-2" />}
                                {/* Use generic icons for Instagram/Facebook */}
                                {l.icon === 'instagram' && <Share2 className="h-4 w-4 mr-2" />}
                                {l.icon === 'facebook' && <Share2 className="h-4 w-4 mr-2" />}
                                {l.label}
                              </a>
                            </DropdownMenuItem>
                          ))}
                          {buildContactLinks(detail.shelter).length === 0 && (
                            <DropdownMenuItem disabled>No hay redes disponibles</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </Layout>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M15.1 2.2c0 1-.4 2-1.1 2.8-.8.9-1.9 1.4-2.9 1.3-.1-1 .3-2.1 1-2.8.8-.8 2-1.4 3-1.3ZM18.8 17.8c-.5 1.1-.8 1.6-1.4 2.6-.8 1.3-1.9 2.9-3.2 2.9-1.2 0-1.5-.8-3.1-.8-1.5 0-1.9.8-3.1.8-1.3 0-2.3-1.4-3.1-2.7-2.2-3.4-2.5-7.3-1.1-9.4 1-1.5 2.5-2.4 4-2.4 1.6 0 2.6.8 3.9.8 1.3 0 2.1-.8 3.9-.8 1.4 0 2.9.8 3.9 2.2-3.4 1.9-2.8 6.7.3 8.8Z" />
    </svg>
  );
}

function GooglePlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path fill="#34A853" d="M3.8 3.2 13.6 13 3.8 20.8c-.3-.2-.5-.6-.5-1V4c0-.4.2-.7.5-.8Z" />
      <path fill="#4285F4" d="m16.8 16.2-3.2-3.2L3.8 3.2c.2-.1.6-.1.9.1l12.1 6.8Z" />
      <path fill="#FBBC04" d="m16.8 16.2-12.1 6.9c-.3.2-.6.2-.9.1l9.8-9.8Z" />
      <path fill="#EA4335" d="M20.2 11.1c.7.4.7 1.4 0 1.8l-3.4 1.9-3.2-3.2 3.2-3.2Z" />
    </svg>
  );
}

function SocialLink({ href, label, icon }: { href: string; label: string; icon: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm text-slate-700 transition hover:border-primary/30 hover:text-primary"
    >
      {icon}
      <span>{label}</span>
    </a>
  );
}
